/**
 * Wishlist Cookie Utilities - Client Side
 *
 * Browser-side cookie operations for wishlist persistence.
 * Enables SSR to read wishlist items from cookies.
 *
 * The server-side utilities in ./cookies.ts use Next.js cookies() API,
 * while these client utilities use document.cookie for browser access.
 */

import {
  WISHLIST_COOKIE_MAX_AGE,
  WISHLIST_COOKIE_PREFIX,
  type WishlistCookieItem,
} from './cookies.constants';

// Re-export the type for convenience
export type { WishlistCookieItem };

/**
 * Get the cookie name for a specific country
 */
function getCookieName(countryCode: string): string {
  return `${WISHLIST_COOKIE_PREFIX}${countryCode.toUpperCase()}`;
}

/**
 * Set wishlist items in cookie (client-side)
 *
 * @param countryCode - The country code (e.g., 'US', 'GB')
 * @param items - Array of wishlist items to store
 */
export function setWishlistCookie(countryCode: string, items: WishlistCookieItem[]): void {
  if (typeof document === 'undefined') return;

  const cookieName = getCookieName(countryCode);
  const isSecure = window.location.protocol === 'https:';
  const secureFlag = isSecure ? '; Secure' : '';

  // Encode as JSON
  const value = JSON.stringify(items);

  document.cookie = `${cookieName}=${encodeURIComponent(value)}; Max-Age=${WISHLIST_COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secureFlag}`;
}

/**
 * Get wishlist items from cookie (client-side)
 *
 * @param countryCode - The country code
 * @returns Array of wishlist items or empty array if not found
 */
export function getWishlistCookie(countryCode: string): WishlistCookieItem[] {
  if (typeof document === 'undefined') return [];

  const cookieName = getCookieName(countryCode);
  const cookies = document.cookie.split(';');

  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === cookieName && value) {
      try {
        return JSON.parse(decodeURIComponent(value)) as WishlistCookieItem[];
      } catch {
        return [];
      }
    }
  }

  return [];
}

/**
 * Clear wishlist cookie (client-side)
 *
 * @param countryCode - The country code to clear, or undefined to clear all
 */
export function clearWishlistCookie(countryCode?: string): void {
  if (typeof document === 'undefined') return;

  if (countryCode) {
    // Clear specific country
    const cookieName = getCookieName(countryCode);
    document.cookie = `${cookieName}=; Max-Age=0; Path=/`;
  } else {
    // Clear all wishlist cookies
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name] = cookie.trim().split('=');
      if (name.startsWith(WISHLIST_COOKIE_PREFIX)) {
        document.cookie = `${name}=; Max-Age=0; Path=/`;
      }
    }
  }
}

/**
 * Sync localStorage wishlist to cookie
 * Call this whenever localStorage changes to keep cookie in sync
 *
 * @param countryCode - The country code
 * @param skuMap - The current wishlist from localStorage
 */
export function syncWishlistToCookie(
  countryCode: string,
  skuMap: Record<string, { sku: string; type?: string }>
): void {
  const items: WishlistCookieItem[] = Object.values(skuMap).map((item) => ({
    sku: item.sku,
    type: item.type || 'watch',
  }));

  setWishlistCookie(countryCode, items);
}
